/* prettier-ignore */

import { computed, inject, Injectable, signal } from '@angular/core';
import { Message } from '../interface/message.interface';
import { ChatStyles } from '../interface/chatStyles.interface';
import { generateToken } from '../helpers/helpers';
import { CookieService } from "ngx-cookie-service";
import { TranslationService } from 'src/app/services';
import { pickByLanguage } from 'src/app/services/helpers/language.util';
import { v4 as uuidv4 } from 'uuid';

@Injectable({ providedIn: 'root' })
export class DataChatService {
  private cookieService: CookieService = inject(CookieService);
  private _translationService = inject(TranslationService);

  public name = signal<string>('ChatBot');
  public id = signal('');
  public token = signal<string | undefined>('');
  public messages = signal<Message[]>([]);
  public chatIcon = signal<string>('/assets/imgs/chatbot-avatar.png');
  public icon = signal<string | undefined>('');
  public logo = signal<string | undefined>('');

  // Metadatos de tarjeta sembrados desde acciones del asistente (SHOW_POI/SHOW_POIS):
  // permiten a <link-preview> pintar con el nombre/imagen reales sin depender del
  // /api/link-preview (que en el self-hosted scrapea y devuelve título genérico). Clave = URL.
  public linkPreviewMeta = new Map<string, { title?: string; description?: string; image?: string; site_name?: string }>();

  // CMAD-189: mensaje informativo configurable desde el CMS (título del disclaimer +
  // descripción de la modal). La API lo devuelve como mapa por idioma (mismo patrón que
  // recommended_messages) y aquí se resuelve reactivamente con el idioma actual.
  // Vacío = usar los textos i18n hardcodeados de siempre.
  public infoMessageTitle = signal<Record<string, string> | string>({});
  public infoMessageDescription = signal<Record<string, string> | string>({});
  public infoMessageTitleCurrentLang = computed(() => this.pickByCurrentLang(this.infoMessageTitle()));
  public infoMessageDescriptionCurrentLang = computed(() => this.pickByCurrentLang(this.infoMessageDescription()));

  private pickByCurrentLang(value: Record<string, string> | string | null | undefined): string {
    if (!value) return '';
    // Compatibilidad con la API antigua, que devolvía el texto ya resuelto.
    if (typeof value === 'string') return value;
    // El CMS puede devolver las claves con variante regional (pt-BR, es-AR): sin
    // resolver, el acceso exacto dejaba el texto vacío sin dar ningún error.
    return pickByLanguage(value, this.lang()) ?? '';
  }

  public showRecommendedMessages = signal<boolean>(true);
  public recommendedMessages = signal<Record<string, string[]>>(null);
  public recommendedMessagesCurrentLang = computed(() => pickByLanguage(this.recommendedMessages(), this.lang()) ?? []);

  public initialMessage = signal<Record<string, string>>(null);
  public initialMessageCurrentLang = computed(() => pickByLanguage(this.initialMessage(), this.lang()) ?? null);
  public abortSignal = signal<AbortController>(new AbortController());
  
  public isStreaming = signal<boolean>(false);
  public chatIsThinking = signal<boolean>(false);
  public isSendingChat = signal<boolean>(false);

  public isPlaying = signal(false);
  public isPlayingOnDemand = signal<string | null>(null);
  
  public isFullScreen = signal<boolean>(false);
  public canSaveChat = signal<boolean>(false);
  public hasHistory = signal<boolean>(false);
  public showTooltip = signal<boolean>(false);
  public hideChat = signal<boolean>(true);

  public inputText = signal(null)
  public selectedImage = signal<File | null>(null);
  public imagePreviewUrl = signal<string | null>(null);

  // POI Sidebar Logic
  public poiList = signal<any[]>([]);
  public activePoiIndex = signal<number>(0);
  public isPoiSidebarOpen = signal<boolean>(false);

  // True once the chat config (styles/brand colors) has loaded → avoids showing
  // the chat with default (gray) styling before the brand color is applied.
  public configLoaded = signal<boolean>(false);

  // Geocoding region for the planner, parametrizable per client (e.g. embed ?city=Madrid).
  // Used as the highest-priority context when there is no site device (embed widget).
  public geocodingCity = signal<string | null>(null);

  // True while the FIRST route is being generated in the embed (between submit and
  // the route arriving). Re-syncs use EditRouteService.isLoadingEditedCustomRouteData,
  // but the initial generation sets the route data directly without that flag, so the
  // embed needs this to show a "generating" cue. Embed-only (the site navigates away).
  public generatingRoute = signal<boolean>(false);

  public allowSendImage = signal(false);
  public allowSendAudio = signal(false);
  public allowProactiveBubbles = signal(false);
  public allowShowMultiItemsModal = signal(true);

  public chatStyles: ChatStyles = {
    header_color: '',
    text_header_color: '',
    user_message_color: '',
    user_text_color: '',
    user_border_color: '',
    user_gradient_1: '',
    bot_message_color: '',
    bot_text_color: '',
    bot_border_color: '',
    bot_gradient_1: '',
    bot_gradient_2: '',
    background_color: '',
    button_color: '',
    recommended_messages_color: '',
    recommended_messages_text_color: '',
    recommended_messages_border_color: '',
  };

  private lang = this._translationService.selectedLanguage;

  // @i18n-keys
  public thinkingPhrases = [
    'Preparando respuesta',
    'Procesando',
    'Pensando',
    'Calculando',
    'Preparando',
    'Generando respuesta',
    'Elaborando',
    'Procesando datos',
    'Organizando info',
    'Preparando contenido',
    'Calentando motores',
    'Configurando',
    'Preparando magia',
    'Generando',
    'Elaborando respuesta',
    'Procesando info',
    'Organizando datos',
    'Preparando solución',
    'Calculando opciones',
    'Generando plan'
  ];

  // @i18n-keys — shown in the chat while the route is being generated (get-route-planning,
  // which can take a while); makes it clear the assistant is building the itinerary.
  public routeThinkingPhrases = [
    'Generando tu ruta',
    'Buscando los mejores sitios',
    'Calculando el itinerario',
    'Organizando los días',
    'Optimizando el recorrido',
    'Casi lista'
  ];

  constructor() {
    this.checkCookies();
   }

  public resetChat() {
    this.messages.set([])
    this.token.set(generateToken());
  }

  public showConnectionErrorMessage() {
    if (this.messages()?.[this.messages()?.length - 1].danger)
      return;

    this.messages.update((prev) => [
      ...prev,
      {
        // id local: el @for del bubble trackea por message.id (HRS-3045)
        id: uuidv4(),
        content: this._translationService.instant('Fallo en la comunicación, vuelve a intentarlo'),
        role: 'assistant',
        show: true,
        danger: true,
      },
    ]);
  }

  public removeErrorMsgs() {
    this.messages.update((prev) =>
      prev.filter((msg) => !msg.danger)
    );
  }
// -------------------------- COOKIES START CONFIG ------------------------------
  public checkCookies(){
    this.checkAllowSendImageCookie();
    this.checkAllowSendAudioCookie();
    this.checkAllowProactiveBubbles();
    // this.checkAllowShowMultiItemsModal();
  }

  private checkAllowSendImageCookie(){
    const allowSendImage = this.cookieService.get('allowSendImage');
    switch(allowSendImage){
      case 'true': this.allowSendImage.set(true); break;
      case 'false': this.allowSendImage.set(false); break;
    }
  }

  private checkAllowSendAudioCookie(){
    const allowSendAudio = this.cookieService.get('allowSendAudio');
    switch(allowSendAudio){
      case 'true': this.allowSendAudio.set(true); break;
      case 'false': this.allowSendAudio.set(false); break;
    }
  }

  private checkAllowProactiveBubbles(){
    const allowProactiveBubbles = this.cookieService.get('allowProactiveBubbles');
    switch(allowProactiveBubbles){
      case 'true': this.allowProactiveBubbles.set(true); break;
      case 'false': this.allowProactiveBubbles.set(false); break;
    }
  }

  // private checkAllowShowMultiItemsModal(){
  //   const allowShowMultiItemsModal = this.cookieService.get('allowShowMultiItemsModal');
  //   switch(allowShowMultiItemsModal){
  //     case 'true': this.allowShowMultiItemsModal.set(true); break;
  //     case 'false': this.allowShowMultiItemsModal.set(false); break;
  //   }
  // }

}